import { Link } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef } from "react";
import {
  LayoutDashboard, TrendingUp, Package, Users, Store, Network, ShoppingCart, Truck, Boxes, ClipboardCheck,
  RotateCcw, Coins, ReceiptText, BadgeDollarSign, ScanLine, ShieldCheck, PackageCheck,
  Landmark, HeartPulse, Megaphone, ArrowDownToLine, WalletCards, Repeat2, UserCog, Factory, PackageOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GineeReminder from "./GineeReminder";
import { useModules } from "@/lib/useModules";
import "./SidebarTheme.css";

const SIDEBAR_SCROLL_KEY = "jayerindo.sidebar.scrollTop";
let sidebarScrollMemory = 0;

export function AppTopBar({ active }) {
  const { user, logout } = useAuth();
  const isOwner = user?.role === "OWNER";
  const { can: canModule, ready: modulesReady } = useModules();
  const sidebarRef = useRef(null);

  useLayoutEffect(() => {
    const el = sidebarRef.current;
    if (!el) return undefined;
    const stored = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY));
    const saved = Number.isFinite(stored) ? stored : sidebarScrollMemory;
    const target = Math.max(0, saved || sidebarScrollMemory || 0);
    el.scrollTop = target;
    const raf1 = window.requestAnimationFrame(() => {
      el.scrollTop = target;
      const raf2 = window.requestAnimationFrame(() => { el.scrollTop = target; });
      el.dataset.sidebarRestoreRaf = String(raf2);
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      const raf2 = Number(el.dataset.sidebarRestoreRaf || 0);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [active, modulesReady]);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return undefined;
    const remember = () => {
      const value = el.scrollTop || 0;
      sidebarScrollMemory = value;
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(value));
    };
    el.addEventListener("scroll", remember, { passive: true });
    return () => el.removeEventListener("scroll", remember);
  }, []);

  const rememberSidebarPosition = () => {
    if (sidebarRef.current) {
      const value = sidebarRef.current.scrollTop || 0;
      sidebarScrollMemory = value;
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(value));
    }
  };

  const can = (mod) => isOwner || canModule(mod);

  const GROUPS = [
    { title: "Dashboard", items: [
      { key: "erp-dashboard", to: "/dashboard", label: "Dashboard ERP", icon: LayoutDashboard, show: isOwner },
      { key: "erp-health", to: "/erp-health", label: "ERP Health", icon: HeartPulse, show: isOwner },
      { key: "analytics", to: "/analytics", label: "Demand Intelligence", icon: TrendingUp, show: isOwner },
    ] },
    { title: "Master", items: [
      { key: "sku", to: "/", label: "Master SKU", icon: Package, show: can("MASTER_SKU") },
      { key: "customers", to: "/customers", label: "Master Konsumen", icon: Users, show: can("INVENTORY") },
      { key: "suppliers", to: "/suppliers", label: "Master Supplier", icon: Factory, show: isOwner },
      { key: "stores", to: "/stores", label: "Master Toko", icon: Store, show: isOwner },
      { key: "store-mappings", to: "/store-mappings", label: "Mapping Toko", icon: Network, show: isOwner },
    ] },
    { title: "Inventory / Operasional", items: [
      { key: "purchase", to: "/purchases", label: "Purchase", icon: ShoppingCart, show: can("PURCHASE") },
      { key: "receiving", to: "/receiving", label: "Penerimaan", icon: Truck, show: can("RECEIVING") },
      { key: "inventory", to: "/inventory", label: "Inventory", icon: Boxes, show: can("INVENTORY") },
      { key: "opname", to: "/inventory/opname", label: "Stock Opname", icon: ClipboardCheck, show: can("INVENTORY") },
      { key: "returns", to: "/inventory/returns", label: "Retur Pembeli", icon: RotateCcw, show: can("INVENTORY") },
      { key: "cancel-refund", to: "/finance/cancel-refund", label: "Cancel / Refund Return", icon: RotateCcw, show: isOwner },
      { key: "repack", to: "/inventory/repack", label: "Repack", icon: PackageOpen, show: can("REPACK") },
    ] },
    { title: "Inventory / Setting", items: [
      { key: "opening-hpp", to: "/inventory/opening-hpp", label: "HPP Awal (Setup)", icon: Coins, show: isOwner },
      { key: "costing", to: "/inventory/costing", label: "Costing Recipe", icon: Coins, show: isOwner },
    ] },
    { title: "Sales", items: [
      { key: "sales", to: "/inventory/manual-sales", label: "Manual Sales", icon: ReceiptText, show: can("INVENTORY") },
      { key: "orders", to: "/orders", label: "Order Ginee", icon: BadgeDollarSign, show: can("ORDER_GINEE") },
    ] },
    { title: "Checking", items: [
      { key: "checker", to: "/checker", label: "Checker", icon: ScanLine, show: can("CHECKER") },
      { key: "final", to: "/final", label: "Final Check", icon: ShieldCheck, show: can("FINAL_CHECK") },
      { key: "dispatch", to: "/dispatch", label: "Paket Keluar", icon: PackageCheck, show: can("FINAL_CHECK") && user?.role !== "ADMIN_INVENTORY" },
    ] },
    { title: "Finance", items: [
      { key: "accounts", to: "/accounts", label: "Dashboard Keuangan", icon: Landmark, show: can("FINANCE") },
      { key: "finance-reports", to: "/finance/reports", label: "Laporan Keuangan", icon: ReceiptText, show: isOwner },
      { key: "reports", to: "/reports", label: "Health Check & Audit", icon: HeartPulse, show: isOwner },
      { key: "ads", to: "/marketplace-ads", label: "Iklan Marketplace", icon: Megaphone, show: isOwner },
    ] },
    { title: "Transaksi Keuangan", items: [
      { key: "finance-withdrawal", to: "/finance/withdrawal", label: "Penarikan Dana", icon: ArrowDownToLine, show: isOwner },
      { key: "finance-expense", to: "/finance/expense", label: "BCA OUT / Biaya", icon: WalletCards, show: isOwner },
      { key: "supplier-deposits", to: "/finance/supplier-deposits", label: "Supplier Deposit", icon: Landmark, show: isOwner },
      // AP Opening is historical cutoff tooling; hidden from operational navigation.
    ] },
    { title: "System", items: [
      { key: "users", to: "/users", label: "Pengguna & Akses", icon: UserCog, show: isOwner },
    ] },
  ];

  return (
    <>
      <GineeReminder enabled={Boolean(user)} canConfigure={isOwner} />
      <aside ref={sidebarRef} className="app-sidebar no-print" data-testid="app-sidebar">
        <div className="sidebar-brand"><span className="brand-mark sm">INV</span><div><div className="biz-name" data-testid="business-name">{user?.business_name}</div><div className="biz-sub">Inventory System</div></div></div>
        <nav className="sidebar-nav">
          {GROUPS.map((g) => {
            const items = g.items.filter((it) => it.show);
            if (items.length === 0) return null;
            return <div className="nav-group" key={g.title}><div className="nav-group-title">{g.title}</div>{items.map((it) => { const Icon = it.icon; return <Link key={it.key} to={it.to} onMouseDown={rememberSidebarPosition} onClick={rememberSidebarPosition} className={`side-link ${active === it.key ? "active" : ""}`} data-testid={`nav-${it.key}`}><Icon className="nav-icon" aria-hidden="true" /><span>{it.label}</span></Link>; })}</div>;
          })}
        </nav>
      </aside>
      <header className="app-topbar-min no-print" data-testid="topbar">
        <div className="topbar-reserved" aria-hidden="true"><span className="topbar-placeholder" title="Segera hadir">Laporan <em>Segera</em></span><span className="topbar-placeholder" title="Segera hadir">Multi Usaha <em>Segera</em></span></div>
        <div className="topbar-right"><span className="role-badge" data-testid="user-role">{user?.role}</span><span className="user-email">{user?.email}</span><button className="btn-ghost" onClick={logout} data-testid="logout-button">Keluar</button></div>
      </header>
    </>
  );
}

export function formatIDR(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(n);
}

// Unit rate (tarif / pcs): show 2 decimals only when fractional (187,50), integer otherwise (188). Display only.
export function formatRate(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  const frac = Math.abs(n - Math.round(n)) > 1e-9;
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: frac ? 2 : 0, maximumFractionDigits: 2 }).format(n);
}
